import { getRum } from './instrumentation.client'

// Client half of Server-Timing (VIT-17752). The header itself is only visible one
// request at a time in devtools; this forwards the navigation's metrics to Datadog
// RUM so they can be aggregated — p75 SSR time by route, edge cache-hit rate as
// browsers actually experienced it, cold-start share of slow page loads.
//
// The metrics come from two layers and arrive in one header: the SSR app's own
// marks (app/server-timing.server.ts) and CloudFront's `cdn-*` metrics
// (sst.config.ts). vit.al serves both from the same origin as the document, so
// `PerformanceServerTiming` is exposed to JS without needing Timing-Allow-Origin.

type ServerTimingAttributes = Record<string, number | string | boolean>

/**
 * Flatten `PerformanceServerTiming` entries into RUM action attributes.
 *
 * A metric carries either a duration (`app;dur=42`), a description
 * (`cdn-pop;desc="AKL50-C1"`), or neither (`cdn-cache-hit` — the name alone is
 * the signal), so each shape maps to the attribute value that stays queryable:
 * a number, a string, or `true`.
 */
export const toServerTimingAttributes = (entries: readonly PerformanceServerTiming[]): ServerTimingAttributes => {
  const attributes: ServerTimingAttributes = {}

  for (const { name, duration, description } of entries) {
    if (name === '') {
      continue
    }
    if (duration > 0) {
      attributes[name] = Math.round(duration * 10) / 10
    } else if (description !== '') {
      attributes[name] = description
    } else {
      attributes[name] = true
    }
  }

  return attributes
}

/**
 * Report the current document's Server-Timing metrics to RUM. A no-op when RUM
 * isn't live (non-production), when the browser doesn't expose the navigation
 * entry, or when no metrics arrived (a prerendered page served from S3 carries
 * none).
 */
export const reportNavigationServerTiming = (): void => {
  const rum = getRum()
  if (rum === undefined) {
    return
  }

  // `getEntriesByType('navigation')` is the document's own request; `serverTiming`
  // is populated as soon as its response headers are in, so this is already
  // available by the time the client entry runs.
  //
  // The DOM lib types the return as plain `PerformanceEntry`, and `serverTiming`
  // is optional in practice (it landed in Safari well after the other navigation
  // fields), so it is narrowed here rather than asserted as always present.
  const [navigation] = performance.getEntriesByType('navigation') as (PerformanceEntry & {
    serverTiming?: readonly PerformanceServerTiming[]
  })[]
  const entries = navigation?.serverTiming ?? []
  if (entries.length === 0) {
    return
  }

  rum.addAction('server_timing', {
    ...toServerTimingAttributes(entries),
    // The route pattern isn't known here (this runs before hydration), so the
    // path is what joins these numbers to a page.
    path: window.location.pathname,
  })
}
